import React, { useEffect, useState, useCallback } from 'react';
import './DeviceSelectorModal.css';

/* Placeholder sample Salesforce data structure (to be replaced by real fetch):
   buildings = [ { id, name, units: [ { id, name, deviceIds: [] } ] } ]
*/
const sampleSalesforceData = [
  {
    id: 'bldgA',
    name: 'Building A',
    units: [
      { id: 'A-101', name: 'Unit A-101', deviceIds: ['DEV_A101_1','DEV_A101_2'] },
      { id: 'A-102', name: 'Unit A-102', deviceIds: ['DEV_A102_1'] }
    ]
  },
  {
    id: 'bldgB',
    name: 'Building B',
    units: [
      { id: 'B-201', name: 'Unit B-201', deviceIds: ['DEV_B201_1','DEV_B201_2','DEV_B201_3'] }
    ]
  }
];

const RECENT_GROUPS_KEY = 'recentDeviceGroups';
const MAX_RECENT_GROUPS = 8;
const SFDC_CACHE_KEY = 'sfdcBuildingsCache';
const SFDC_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 1 day

function isValidCoreid(coreid) {
  if (!coreid || typeof coreid !== 'string') return false;
  return /^[0-9a-fA-F]{24}$/.test(coreid);
}

function loadRecentGroups() {
  try {
    const raw = localStorage.getItem(RECENT_GROUPS_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch { return []; }
}

function saveRecentGroup(group) {
  const existing = loadRecentGroups();
  const filtered = existing.filter(g => g.name !== group.name || g.ids.join(',') !== group.ids.join(','));
  filtered.unshift(group); // add newest at front
  while (filtered.length > MAX_RECENT_GROUPS) filtered.pop();
  localStorage.setItem(RECENT_GROUPS_KEY, JSON.stringify(filtered));
}

function loadSfdcCache() {
  try {
    const raw = localStorage.getItem(SFDC_CACHE_KEY);
    if (!raw) return null;
    const cache = JSON.parse(raw);
    const age = Date.now() - cache.timestamp;
    return { data: cache.data, isStale: age > SFDC_CACHE_MAX_AGE_MS, age };
  } catch {
    return null;
  }
}

function saveSfdcCache(data) {
  try {
    const cache = { data, timestamp: Date.now() };
    localStorage.setItem(SFDC_CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    console.warn('Failed to save SFDC cache:', e);
  }
}

export default function DeviceSelectorModal({ onClose, onApply, existingIds, ws }) {
  const [activeTab, setActiveTab] = useState('manual'); // manual|recent|salesforce
  const [manualInput, setManualInput] = useState(existingIds || '');
  const [recentGroups, setRecentGroups] = useState([]);
  const [sfData, setSfData] = useState([]);
  const [sfLoading, setSfLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [groupName, setGroupName] = useState('');

  // Load recent groups on mount
  useEffect(() => { setRecentGroups(loadRecentGroups()); }, []);

  // Load SFDC data from cache immediately, fetch if stale
  useEffect(() => {
    if (activeTab === 'salesforce' && sfData.length === 0) {
      // Try to load from cache first
      const cache = loadSfdcCache();
      if (cache && cache.data) {
        console.log(`Loaded SFDC data from cache (age: ${Math.round(cache.age / 1000 / 60)} minutes, stale: ${cache.isStale})`);
        setSfData(cache.data);
        
        // If cache is stale, also fetch fresh data in background
        if (cache.isStale && !sfLoading && ws && ws.readyState === WebSocket.OPEN) {
          console.log('Cache is stale, fetching fresh SFDC data in background...');
          setSfLoading(true);
          ws.send(JSON.stringify({ action: '/load-sfdc-systems' }));
        }
      } else if (!sfLoading && ws && ws.readyState === WebSocket.OPEN) {
        // No cache, fetch fresh data
        console.log('No SFDC cache found, fetching fresh data...');
        setSfLoading(true);
        ws.send(JSON.stringify({ action: '/load-sfdc-systems' }));
      } else if (!ws || ws.readyState !== WebSocket.OPEN) {
        console.warn('WebSocket not connected; cannot load SFDC data.');
      }
    }
  }, [activeTab, sfData, sfLoading, ws]);

  // Listen for SFDC data response via custom event
  useEffect(() => {
    const handleSfdcData = (e) => {
      const { buildings } = e.detail;
      // Normalize and filter data client-side
      const normalized = (buildings || []).map(b => ({
        ...b,
        name: (b.name || '').trim() || 'N/A',
        units: (b.units || []).map(u => ({
          ...u,
          name: (u.name || '').trim() || 'N/A',
          systems: (u.systems || []).filter(s => isValidCoreid(s.coreid))
        })).filter(u => u.systems.length > 0)
      })).filter(b => b.units.length > 0);
      
      // Save to cache
      saveSfdcCache(normalized);
      console.log('SFDC data cached to localStorage');
      
      setSfData(normalized);
      setSfLoading(false);
    };
    window.addEventListener('sfdcSystemsLoaded', handleSfdcData);
    return () => window.removeEventListener('sfdcSystemsLoaded', handleSfdcData);
  }, []);

  const parseManualIds = useCallback(() => {
    return manualInput
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }, [manualInput]);

  const applySelection = () => {
    const combined = Array.from(new Set(selectedIds));
    onApply(combined);
    onClose();
  };

  const addManualToSelection = () => {
    const ids = parseManualIds();
    setSelectedIds(prev => Array.from(new Set([...prev, ...ids])));
  };

  const saveCurrentSelectionAsGroup = () => {
    const ids = Array.from(new Set(selectedIds));
    if (!groupName.trim() || ids.length === 0) return;
    const group = { name: groupName.trim(), ids, timestamp: Date.now() };
    saveRecentGroup(group);
    setRecentGroups(loadRecentGroups());
    setGroupName('');
  };

  const useGroup = (group) => {
    setSelectedIds(group.ids);
  };

  const deleteGroup = (group) => {
    const existing = loadRecentGroups();
    const filtered = existing.filter(g => g.timestamp !== group.timestamp);
    localStorage.setItem(RECENT_GROUPS_KEY, JSON.stringify(filtered));
    setRecentGroups(filtered);
  };

  const addUnitDevices = (unit) => {
    const coreids = unit.systems.map(s => s.coreid).filter(Boolean);
    setSelectedIds(prev => Array.from(new Set([...prev, ...coreids])));
  };

  const addBuildingDevices = (building) => {
    const allIds = building.units.flatMap(u => u.systems.map(s => s.coreid).filter(Boolean));
    setSelectedIds(prev => Array.from(new Set([...prev, ...allIds])));
  };

  const removeId = (id) => {
    setSelectedIds(prev => prev.filter(x => x !== id));
  };

  const clearSelection = () => {
    setSelectedIds([]);
  };

  const handleOverlayClick = (e) => {
    if (e.target.className === 'device-selector-overlay') {
      onClose();
    }
  };

  return (
    <div className="device-selector-overlay" role="dialog" aria-modal="true" onClick={handleOverlayClick}>
      <div className={`device-selector-modal ${sfData.length > 0 ? 'sfdc-loaded' : ''}`}>
        <div className="device-selector-header">
          <h2>Select Devices</h2>
          <button className="device-selector-close" onClick={onClose}>✕</button>
        </div>
        <div className="device-selector-tabs">
          {['manual','recent','salesforce'].map(tab => (
            <div
              key={tab}
              className={`device-selector-tab ${activeTab === tab ? 'active' : ''}`.trim()}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'manual' && 'Manual'}
              {tab === 'recent' && 'Recent Groups'}
              {tab === 'salesforce' && 'Salesforce'}
            </div>
          ))}
        </div>
        <div className="device-selector-content">
          {activeTab === 'manual' && (
            <div className="selector-section manual-input">
              <h3>Manual Entry</h3>
              <textarea
                value={manualInput}
                onChange={e => setManualInput(e.target.value)}
                placeholder="Enter comma-separated device IDs..."
              />
              <div style={{ display:'flex', gap:'8px', marginTop:'8px' }}>
                <button className="btn" type="button" onClick={addManualToSelection}>Add to Selection</button>
                <button className="btn btn-secondary" type="button" onClick={() => setManualInput('')}>Clear Field</button>
              </div>
              <small>Device IDs added this way are merged into the current selection.</small>
            </div>
          )}
          {activeTab === 'recent' && (
            <div className="selector-section">
              <h3>Recent Groups</h3>
              <div className="group-list">
                {recentGroups.length === 0 && <div style={{ opacity:.6 }}>No recent groups yet.</div>}
                {recentGroups.map(g => (
                  <div className="group-item" key={g.timestamp}>
                    <div className="group-item-info">
                      <strong>{g.name}</strong>
                      <span>{g.ids.length} devices</span>
                      <span style={{ opacity:.6 }}>Saved {new Date(g.timestamp).toLocaleString()}</span>
                    </div>
                    <div style={{ display:'flex', gap:'6px' }}>
                      <button className="btn btn-logout" type="button" onClick={() => deleteGroup(g)}>Delete Group</button>
                      <button className="btn" type="button" onClick={() => useGroup(g)}>Use Group</button>
                      <button className="btn btn-secondary" type="button" onClick={() => setSelectedIds(prev => Array.from(new Set([...prev, ...g.ids])))}>Merge</button>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:'12px', display:'flex', gap:'8px', alignItems:'center' }}>
                <input
                  type="text"
                  placeholder="Name current selection..."
                  value={groupName}
                  onChange={e => setGroupName(e.target.value)}
                  style={{ flex:1, background:'#1e1e1e', border:'1px solid #555', borderRadius:'6px', color:'var(--text-color)', padding:'6px', fontSize:'12px' }}
                />
                <button className="btn" type="button" onClick={saveCurrentSelectionAsGroup} disabled={!groupName.trim() || selectedIds.length===0}>Save Group</button>
              </div>
              <small>Groups persist locally in your browser.</small>
            </div>
          )}
          {activeTab === 'salesforce' && (
            <div className="selector-section">
              <h3>Salesforce (Buildings / Units)</h3>
              {sfLoading && <div style={{ opacity:.6, marginBottom:'8px' }}>Loading SFDC data...</div>}
              {!sfLoading && sfData.length === 0 && <div style={{ opacity:.6 }}>No data loaded yet.</div>}
              {sfData.length > 0 && (
                <>
                  <div style={{ marginBottom:'12px' }}>
                    <small style={{ opacity:0.8, marginBottom:'8px', display:'block' }}>Click building name to add all devices in that building:</small>
                    <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                      {[...sfData].sort((a, b) => a.name.localeCompare(b.name)).map(b => (
                        <button key={b.id} type="button" className="btn" onClick={() => addBuildingDevices(b)}>
                          {b.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="building-unit-grid">
                    {[...sfData].sort((a, b) => a.name.localeCompare(b.name)).map(b => b.units.map(u => (
                      <div className="bu-card" key={b.id + u.id}>
                        <strong>{b.name} / {u.name}</strong>
                        <span>{u.systems.length} system(s)</span>
                        <button type="button" className="btn" onClick={() => addUnitDevices(u)}>Add Unit</button>
                      </div>
                    )))}
                  </div>
                </>
              )}
            </div>
          )}
          <div className="selector-section">
            <h3>Current Selection</h3>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
              {selectedIds.length === 0 && <span style={{ opacity:.6 }}>No devices selected.</span>}
              {selectedIds.map(id => (
                <div key={id} style={{ background:'#1e1e1e', border:'1px solid #555', padding:'4px 8px', borderRadius:'16px', display:'flex', alignItems:'center', gap:'6px', fontSize:'11px' }}>
                  <span>{id}</span>
                  <button type="button" onClick={() => removeId(id)} style={{ background:'none', border:'none', color:'var(--text-secondary-color)', cursor:'pointer' }}>✕</button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="device-selector-footer">
          <div className="selection-summary">{selectedIds.length} device(s) selected.</div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button className="btn btn-secondary" type="button" onClick={clearSelection} disabled={selectedIds.length===0}>Clear Selection</button>
            <button className="btn" type="button" disabled={selectedIds.length===0} onClick={applySelection}>Apply Selection</button>
          </div>
        </div>
      </div>
    </div>
  );
}
